import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { useModeForShell } from "@/hooks/useMode";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Light mode: the deeper action green, so white text reads (the brand
        // green under white text is ~2:1). Dark keeps the brand green.
        default:
          "bg-[hsl(var(--action))] text-[hsl(var(--action-foreground))] hover:bg-[hsl(var(--action)/0.9)] dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-[hsl(var(--action))] underline-offset-4 hover:underline dark:text-primary",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size, asChild = false, ...props }, ref) => {
    const mode = useModeForShell();
    const Comp = asChild ? Slot : "button";

    // Apply blue colors in private mode
    const modeAwareClassName = React.useMemo(() => {
      const baseClasses = buttonVariants({ variant, size });

      if (mode === "private") {
        if (variant === "default") {
          // Override primary green with blue in private mode
          // Tailwind will use the last class, so bg-blue-600 will override bg-primary
          return cn(
            baseClasses,
            "bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-600",
            className
          );
        }
        if (variant === "outline") {
          return cn(
            baseClasses,
            "border-blue-600 text-blue-600 hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-950",
            className
          );
        }
        if (variant === "ghost") {
          return cn(
            baseClasses,
            "hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-950",
            className
          );
        }
        if (variant === "link") {
          return cn(baseClasses, "text-blue-600 hover:text-blue-700", className);
        }
        if (variant === "secondary") {
          return cn(
            baseClasses,
            "bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-400",
            className
          );
        }
      }
      return cn(baseClasses, className);
    }, [mode, variant, size, className]);

    return <Comp className={modeAwareClassName} ref={ref} {...props} />;
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
